import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { InfoContext, MasterContext, UIContext } from "../../../shared/contexts";
import { Form } from "antd";
import TotalProductionService from "../../../shared/services/totalProduction";
import { GetTotalProductionsListServiceResponse, TotalProduction } from "../../../shared/interfaces/services/total-production";
import { PageHeader } from "../../../shared/components/MainLayout/components";
import { CardSkeletons, ColSize, ErrorResults, InputFactory, RowSize } from "../../../shared/components";
import { DownloadOutlined } from "@ant-design/icons";
import { createXLSXFile } from "../../../shared/utils";
import { mapOfTotalProduction } from "./utils/map-of-total-production";
import { Grid } from "@hdi-seguros/web-components.grid";
import { ItemNotFound } from "../../../shared/components/ItemNotFound";
import { TotalProductionCard } from "./components/TotalProductionCard";
import { InsurancePolicyNotFound } from "../../../assets";
import { InputFactoryProp, Intermediary } from "../../../shared/interfaces";
import AuthenticationService from "../../../shared/services/authentication";
import './index.css';

interface TotalProductionProps {
    title: string;
}

export const TotalProductionPage: FC<TotalProductionProps> = ({ title }) => {
    const { isLoading, showError, setIsLoading, setShowError } = useContext( UIContext );
    const { setTitle } = useContext(InfoContext);
    const gridRef = useRef();
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [form] = Form.useForm()
    const [producersCombo, setProducersCombo] = useState<any>([]);
    const [producersInfo, setProducersInfo] = useState<Array<Intermediary>>();
    const [ totalProductionList, setTotalProductionList ] = useState<GetTotalProductionsListServiceResponse>({});
    const [ totalProductionUnique, setTotalProduction ] = useState<Array<TotalProduction>>(totalProductionList.totales || []);
    const { branchList, getBranchsList } = useContext(MasterContext);

    const CURRENCY_COLUMNS = [
        "Prima Emi.",
        "Premio Emi.",
        "Comision Emi.",
        "Prima Cob.",
        "Premio Cob.",
        "Comision Cob.",
      ];

    const getTotalProductionList = async () => {
        setIsLoading(true);
        if(branchList?.length === 0) return;
        
        try {
            const filters = form.getFieldsValue()
            let selectedProducer = undefined;
            if(producersInfo !== undefined && producersInfo.length > 0){
                selectedProducer = producersInfo.find(
                    (producer) => producer.codigoInter === filters.producer
                  );
            }
            const response = await TotalProductionService.getTotalProduction( selectedProducer ); 

            if (response.totales) {
                const updatedTotals = response.totales.map((totalProduction) => ({
                    ...totalProduction,
                    rama: getBranchDescription(totalProduction.rama),
                }));
                setTotalProductionList({ ...response, totales: updatedTotals });
            }
            
        } catch (error) {
            setShowError(true);
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 0);
        }
    }

    const getComboProducer = async () => {
        try {
            const response = await AuthenticationService.getIntermediariesByIntermediary();
            setProducersInfo(response.data.intermediarios.intermediario);
            let comboProducersData = response.data.intermediarios.intermediario.map((producer: any) => ({
                value: producer.codigoInter,
                label: `${producer.nombreInter} (${producer.nivelInter}-${producer.codigoInter})`,
            }));
            
            if (response.status === 200) setProducersCombo(comboProducersData);
        } catch (error) {}
    };

    const getFiltersInfo = useCallback(() => {
            Promise.all([getComboProducer(), getBranchsList()])
            .then(() => {
                setIsLoadingFilters(false);
            })
            .catch(error => {});
    }, []);

    const getBranchDescription = useMemo(() => (branch: string) => {                
        return branchList?.find((b) => b.value === branch)?.label || branch;
    }, [branchList]);
    
    useEffect(() => {
        setIsLoading(true);
        getTotalProductionList();
    }, [branchList]);

    useEffect(() => {
        getFiltersInfo()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setTitle(title);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const actions = useMemo(() => [
        {
            label: 'Descargar',
            icon: <DownloadOutlined />,
            onClick: totalProductionList?.totales?.length ? () => createXLSXFile(mapOfTotalProduction( totalProductionList?.totales ), 'Totales de producción', { currencyColumns: CURRENCY_COLUMNS }) : undefined,
        }
    ], [totalProductionList?.totales]);
    
    useEffect(() => {
        if (totalProductionList) {
            (gridRef.current as any).setPage(1);
        }
    }, [totalProductionList, setTotalProductionList]);

    const initialStateFilters = useMemo(() => ({
        producer: [],
    }), []);


    useEffect(() => {
        form.setFieldsValue(initialStateFilters);
    }, [form, initialStateFilters]);

    const inputsFilter: InputFactoryProp[] = [
        {
          element: 'select',
          placeHolder: 'Productor',
          items: producersCombo,
          allowClear: true,
          name: 'producer',
        }
    ];

    return (
        <div className="total-production">
            <PageHeader actions={actions} />
            <RowSize rowProps={{ gutter: [0, 0] }} className="total-production__filter-row">
                {<InputFactory 
                    form={form}
                    items={ inputsFilter }
                    onFilter={ getTotalProductionList }
                    isLoading={ isLoadingFilters }
                    initialState={ initialStateFilters }
                />}
            </RowSize>
            {
                showError 
                    ? <ErrorResults title="No se pudo cargar el total de producción" />
                    : (
                        <RowSize rowProps={{ gutter: [15, 15], className: "card-margin-fix" }}>
                            <ColSize size={100} className="total-production__grid-padding" >
                                <Grid
                                    list={(totalProductionList?.totales) || []} 
                                    setState={setTotalProduction} 
                                    name="Registros" 
                                    ref={gridRef}
                                >
                                    <ColSize size={100}>
                                        {
                                            totalProductionUnique?.length
                                                ? totalProductionUnique.map(( totalProduction, index ) => (
                                                    <TotalProductionCard
                                                        key={`TotalProduction-${index}`}
                                                        TotalProduction={totalProduction}
                                                        loading={isLoading}
                                                    />
                                                    ))
                                            : isLoading
                                            ? (
                                                <RowSize rowProps={{ gutter: [15, 15] }}>
                                                    <CardSkeletons />
                                                </RowSize>
                                            ) : <ItemNotFound textDescription="No se encontraron totales de producción" imageUrl={InsurancePolicyNotFound} />
                                        }
                                    </ColSize>
                                </Grid>
                            </ColSize>
                        </RowSize>
                    )
                }
        </div>

    )

};

export {};
